import { useResponsiveKeenSliderOptions } from '../../hooks/useResponsiveStackGridOptions'
import { useWindowSize } from '../../hooks/useWindowSize'
import { FullGridWidthDiv, GridWithGaps } from '../../layout/MainGrid'
import type { KeenSliderOptions } from 'keen-slider'
import 'keen-slider/keen-slider.min.css'
import { useKeenSlider } from 'keen-slider/react'
import type { FunctionComponent, LegacyRef, ReactElement } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { LEXICON_SLIDER_SCALE_SIZE } from '../LexiconSlider/LexiconSlider'
import { SliderBottomNavigation } from '../SliderBottomNavigation'
import { SliderChevron } from '../SliderChevrons'
import { LexiconSlides } from './LexiconSlide'

const bottomNavigationWrapperClasses =
  'z-10 flex items-center col-span-12 mt-px-32 ml:mt-px-48'
const accentroSliderWrapperClasses = 'col-span-full relative'
const sliderChevronWrapperClasses = 'flex justify-end ml-px-32 md:ml-px-48'
const wrapperClasses = 'ml-px-4'

export type LexiconSliderProps = {
  slides: Slide[]
  width?: number
  currentSlide?: number
  slidesPerView?: number
  spacing?: number
  minHeight: number
}

export type Slide = {
  title?: string | null
  slide: ReactElement
  active?: boolean
  hasContent?: boolean
}

export const LexiconSliderComponent: FunctionComponent<
  React.PropsWithChildren<LexiconSliderProps>
> = ({ slides, width, currentSlide = 0, minHeight }) => {
  const [slideIndex, setSlideIndex] = useState(currentSlide)
  const { spacing, height } = useResponsiveKeenSliderOptions('LexiconSlider')

  const windowSize = useWindowSize()
  const [slidesPerView, setSlidesPerView] = useState<number>(0)

  const sliderOptions = useMemo(() => {
    return {
      initial: currentSlide,
      mode: 'snap',
      loop: false,
      slides: {
        perView: 'auto',
        spacing: spacing,
      },
      slideChanged: (slider) => {
        setSlideIndex(slider.track.details.rel)
      },
    } as KeenSliderOptions
  }, [currentSlide, spacing])

  const [sliderRef, slider] = useKeenSlider(sliderOptions)

  useEffect(() => {
    slider?.current?.moveToIdx(currentSlide)
  }, [currentSlide, slider])

  useEffect(() => {
    slider?.current?.update()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides?.length])
  const sliderSize =
    slider && slider.current ? slider.current.track.details.slides.length : 0

  //useEffect here I have to check- if size is undefined: Math.floor(windowSize.width / minHeight)
  useEffect(() => {
    if (windowSize.width) {
      setSlidesPerView(Math.floor(windowSize.width / minHeight))
    }
  }, [minHeight, slidesPerView, windowSize.width])

  return (
    <div className={wrapperClasses}>
      <GridWithGaps>
        <div className={accentroSliderWrapperClasses}>
          <FullGridWidthDiv>
            <div className="w-full">
              <div
                ref={sliderRef as LegacyRef<HTMLDivElement>}
                className="keen-slider"
                style={{
                  minHeight: `${minHeight + LEXICON_SLIDER_SCALE_SIZE + 5}px`,
                  alignItems: 'flex-start',
                }}
              >
                <LexiconSlides
                  height={height}
                  width={width}
                  slides={slides}
                  minHeight={minHeight}
                />
              </div>
            </div>
          </FullGridWidthDiv>
        </div>
        <div className={bottomNavigationWrapperClasses}>
          <div className="w-full">
            <SliderBottomNavigation
              optionType={'LexiconSlider'}
              slideIndex={slideIndex}
              slider={slider}
              slidesPerView={!!slidesPerView ? slidesPerView : 1}
            />
          </div>
          <div className={sliderChevronWrapperClasses}>
            <SliderChevron
              slider={slider}
              disabled={slideIndex === 0}
              isLeft
              optionType={'LexiconSlider'}
            />
            <SliderChevron
              optionType={'LexiconSlider'}
              slider={slider}
              disabled={
                !!slidesPerView
                  ? slideIndex === Math.round(sliderSize - slidesPerView)
                  : false
              }
            />
          </div>
        </div>
      </GridWithGaps>
    </div>
  )
}
